// HR policy document API contracts. Shared between web and api.
//
// Binary lives in R2 (POLICY_DOCS bucket); D1 stores metadata only. The list
// of accepted MIME types is intentionally narrow — broaden when an HR
// customer surfaces a real-world document the parser needs to ingest.

import { z } from 'zod';

// Slice 16 — ingestion lifecycle (DESIGN §5.2.1). The state pill renders
// from this field on the HR Policies UI so HR sees "Ready (47 chunks)" /
// "Processing…" / "Failed — corrupt PDF" rather than the upload looking
// complete but invisible to Maeve.
export const POLICY_INGESTION_STATUSES = [
  'Pending',
  'Processing',
  'Ready',
  'Failed',
  'Skipped',
] as const;
export type PolicyIngestionStatus = (typeof POLICY_INGESTION_STATUSES)[number];

export interface PolicyIngestion {
  status: PolicyIngestionStatus;
  chunkCount: number | null;
  lastError: string | null;
}

// Approval gate (DESIGN §6 step 2 / spec §4 "Policy Extraction Review"). A
// policy is `Draft` on upload and only becomes usable by Maeve once an
// HR_Admin reviews the extracted rules + audit and approves it. Mirrors the
// `policyApprovalStatus` D1 enum verbatim.
export const POLICY_APPROVAL_STATUSES = ['Draft', 'Approved'] as const;
export type PolicyApprovalStatus = (typeof POLICY_APPROVAL_STATUSES)[number];

export interface PolicyApproval {
  status: PolicyApprovalStatus;
  // Who approved + when, populated only on `status === 'Approved'`. The actor
  // id may be null even when approved if the approving HR user was later
  // deleted (FK is ON DELETE SET NULL) — the timestamp survives as the trail.
  approvedByUserId: string | null;
  approvedAt: string | null; // ISO 8601
}

export interface PolicySummary {
  id: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  uploadedByUserId: string | null;
  uploadedAt: string; // ISO 8601
  // Approval gate state. Always present (the column is NOT NULL with a
  // `Draft` default), so unlike ingestion/audit this is never null. The HR
  // UI renders a pill + an Approve action for HR_Admins on `Draft` rows.
  approval: PolicyApproval;
  // Null when no ingestion state row exists yet (pre-Slice-16 uploads that
  // haven't been backfilled, or the brief window between R2-put and the
  // Pending state-row commit). The frontend renders the null case as a
  // neutral "not indexed" pill so it doesn't look like an error.
  ingestion: PolicyIngestion | null;
  // Slice 19 — Multi-Agent Policy Audit summary. Same null-means-not-yet
  // posture as `ingestion`: pre-Slice-19 uploads and the brief
  // upload-to-Pending-row window both surface as `null`, rendered as a
  // neutral "not audited" pill rather than an error.
  audit: PolicyAuditSummary | null;
  // PROJECT §4 Step 2 — key-rules extraction summary. Null when the
  // extraction pipeline hasn't been triggered for this policy yet (very
  // brief window between R2-put and the Pending state-row commit, plus
  // pre-Step-2 uploads). The HR Policies list renders a "Rules" pill +
  // review link here; the policy-approval route refuses to flip Draft →
  // Approved while this is in a state that blocks approval.
  rules: PolicyRulesSummary | null;
}

// Manager Policies tab (PO 2026-08-06) — the read-only mirror of HR's
// Policies list. Deliberately a leaner shape than `PolicySummary`: a Manager
// never sees ingestion/audit/rules pipeline state (that's HR's review
// workflow, already resolved by the time a policy is `Approved`), just what
// they need to browse and download.
export interface ApprovedPolicySummary {
  id: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  uploadedAt: string; // ISO 8601
  approvedAt: string | null; // ISO 8601
}

// PROJECT §4 Step 2 — at-a-glance key-rule counts so the HR Policies list
// can render a pill without a second round-trip per row. Computed by the
// repo's `list()` so the JSON shape matches the per-policy extraction
// detail endpoint (`PolicyExtractionReport.summary`) — same `total`,
// `pendingReview`, `approved`, `rejected` field names.
// Single source of truth for "does this policy's key-rule extraction block it
// from being approved?" Used by BOTH the HR Policies list rollup
// (`buildRulesSummary`) and the per-policy extraction report
// (`getReportForPolicy`, which the approve route trusts) so the list pill and
// the approve gate can never disagree. Blocks while the pipeline is mid-flight
// (Pending / Processing / Failed / Unavailable) or any rule is still
// `Pending_Review` on a `Ready` policy. `NoRules` (Ready + 0 rules) and a
// Ready policy whose rules are all terminal (Approved / Rejected) do NOT block.
export function extractionBlocksPolicyApproval(
  extractionStatus: PolicyRulesSummary['extractionStatus'],
  pendingReviewCount: number,
): boolean {
  return (
    extractionStatus === 'Pending' ||
    extractionStatus === 'Processing' ||
    extractionStatus === 'Failed' ||
    extractionStatus === 'Unavailable' ||
    (extractionStatus === 'Ready' && pendingReviewCount > 0)
  );
}

export interface PolicyRulesSummary {
  // `extractionStatus` mirrors the per-policy extraction lifecycle. The
  // `blocksPolicyApproval` field on `PolicySummary` is derived by
  // `repoToSummary` from this + the counts; the front-end reads the flat
  // boolean so the list-view render is a single comparison.
  extractionStatus: 'Pending' | 'Processing' | 'Ready' | 'Failed' | 'Unavailable' | 'NoRules';
  total: number;
  pendingReview: number;
  approved: number;
  rejected: number;
  blocksPolicyApproval: boolean;
}

// Slice 19 — Multi-Agent Policy Audit (DESIGN §5.4) wire types.
//
// Status semantics mirror the D1 column verbatim — see
// `packages/db/src/schema.ts` `policyAuditStatus`. `Unavailable` is the
// distinct "loader refused — Policy_Audit_* prompts not yet signed off by
// legal counsel" state, separated from `Failed` so the HR UI can show
// "Awaiting CGC sign-off" rather than a scary red badge.
export const POLICY_AUDIT_STATUSES = [
  'Pending',
  'Processing',
  'Ready',
  'Failed',
  'Unavailable',
] as const;
export type PolicyAuditStatus = (typeof POLICY_AUDIT_STATUSES)[number];

// Compact summary shown in the HR Policies list row alongside the ingestion
// pill. Full report fetched separately via `GET /api/hr/policies/:id/audit`.
export interface PolicyAuditSummary {
  status: PolicyAuditStatus;
  lastError: string | null;
  // Top-line compliance score from the Supervisor output (0-100). Null on
  // non-Ready states. Useful for the list-row pill so HR can scan the page.
  overallComplianceScore: number | null;
  // Top-line empathy/tone score from the Supervisor output (0-100). Null on
  // non-Ready states.
  overallEmpathyScore: number | null;
  processedAt: string | null; // ISO 8601
}

// =============================================================================
// Multi-Agent structured outputs — DESIGN §5.4.
//
// Each agent returns a Zod-validated JSON object so the Supervisor and the
// HR UI can rely on the wire shape regardless of how the underlying Gemini
// model phrases its findings. The schemas live here in shared so the
// frontend can render directly off them without re-deriving the shape.
// =============================================================================

// One concrete observation from an agent. `severity` discriminates how the
// UI renders it; `policyExcerpt` carries the verbatim text the model is
// commenting on (capped at 500 chars so a verbose model can't bloat the
// row). All free-text fields are length-capped at write time — the cap
// values live alongside the Zod schemas in the audit service.
export type AuditFindingSeverity = 'high' | 'medium' | 'low' | 'informational';

export interface ComplianceFinding {
  severity: AuditFindingSeverity;
  // Short headline ("Statutory bereavement leave not specified").
  title: string;
  // Detailed explanation with the relevant ACAS or Employment Rights Act
  // 2025 anchor. Free text; renderer wraps and preserves whitespace.
  detail: string;
  // The verbatim chunk of policy text the finding refers to, if any.
  // Null when the finding is about an *absence* in the policy.
  policyExcerpt: string | null;
  // Plain-English suggested fix or follow-up action for HR.
  suggestedEdit: string | null;
}

export interface ComplianceAgentOutput {
  // Top-line score 0-100. Lower scores correspond to more (or more severe)
  // findings. The Supervisor uses this verbatim — it does not re-score.
  overallScore: number;
  findings: ComplianceFinding[];
  // One-sentence narrative summary from the Compliance agent. Distinct
  // from the Supervisor's synthesised summary.
  summary: string;
}

export interface EmpathyFinding {
  severity: AuditFindingSeverity;
  title: string;
  detail: string;
  policyExcerpt: string | null;
  suggestedEdit: string | null;
}

export interface EmpathyAgentOutput {
  overallScore: number;
  findings: EmpathyFinding[];
  summary: string;
}

// Final synthesised report — produced by the Supervisor from the parallel
// Compliance + Empathy outputs. The HR UI renders this; the per-agent raw
// outputs are kept in D1 for audit / debugging but are not surfaced in the
// primary read path.
export interface SynthesisedReport {
  // Headline scores carried forward from the agent outputs.
  overallComplianceScore: number;
  overallEmpathyScore: number;
  // Plain-English executive summary for the HR Admin to read first.
  executiveSummary: string;
  // Prioritised list of suggested edits drawn from BOTH agents, with the
  // Supervisor's deduplication + priority ordering applied.
  prioritisedActions: Array<{
    priority: 'critical' | 'high' | 'medium' | 'low';
    action: string;
    rationale: string;
    sourceAgent: 'compliance' | 'empathy' | 'both';
  }>;
}

// Full audit response shape returned by `GET /api/hr/policies/:id/audit`.
export interface PolicyAuditReport {
  policyId: string;
  status: PolicyAuditStatus;
  lastError: string | null;
  processedAt: string | null; // ISO 8601
  // Per-agent raw outputs. Populated only when the agent ran successfully —
  // a partial-failure audit (Compliance succeeded, Empathy failed) will
  // have one filled and one null.
  compliance: ComplianceAgentOutput | null;
  empathy: EmpathyAgentOutput | null;
  // Supervisor synthesis. Only populated on `status === 'Ready'`.
  synthesised: SynthesisedReport | null;
}

export const ALLOWED_POLICY_CONTENT_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
  'application/msword', // .doc
  'text/plain',
  'text/markdown',
] as const;

export type AllowedPolicyContentType = (typeof ALLOWED_POLICY_CONTENT_TYPES)[number];

// 10 MB. Workers limit is 100 MB and HR policy PDFs are typically <2 MB —
// 10 MB leaves headroom without inviting denial-of-wallet uploads.
export const MAX_POLICY_BYTES = 10 * 1024 * 1024;

export const MAX_POLICY_FILENAME_LENGTH = 200;

export function isAllowedPolicyContentType(t: string): t is AllowedPolicyContentType {
  return (ALLOWED_POLICY_CONTENT_TYPES as readonly string[]).includes(t);
}

// ---------------------------------------------------------------------------
// PROJECT §4 Step 2 — Policy key-rules extraction review.
//
// On policy upload the system runs an LLM pass that surfaces the discrete
// rules HR needs to confirm before the policy can be `Approved` (and Maeve
// can cite it for entitlements). The lifecycle is:
//   1. Queue consumer (`Policy_KeyRule_Extraction` prompt, Vertex Gemini,
//      europe-west2) extracts rules and writes one row per rule, all
//      `Pending_Review`.
//   2. HR Admin reviews each rule on the Policies page — approve, reject,
//      or edit-text-then-approve. Terminal rows are write-once for the
//      legal-defensibility audit trail.
//   3. The `policies.approvalStatus` route refuses to flip to `Approved`
//      while any non-rejected rule is still `Pending_Review` — that's the
//      "HR reviews before Maeve uses the policy for guidance" gate the
//      spec §4 Step 2 calls out.
// ---------------------------------------------------------------------------

// Status enum — mirrors `policyRuleStatus` in `@besideu/db` (the schema is
// authoritative for the SQL CHECK / column typing; this copy is the wire
// surface so web/api can import without reaching for db).
export const POLICY_RULE_STATUSES = ['Pending_Review', 'Approved', 'Rejected'] as const;
export type PolicyRuleStatus = (typeof POLICY_RULE_STATUSES)[number];

// Category is free-form text the extraction prompt can grow without a
// schema migration. Common values the V1 prompt produces: `leave`, `pay`,
// `notice`, `return_to_work`, `eligibility`.
export interface PolicyExtractedRule {
  id: string;
  policyId: string;
  // Stable identifier the extractor produces per rule (e.g.
  // `paid_leave_days`). Re-extraction reuses the same key so the UPSERT
  // path in the worker keeps the history.
  ruleKey: string;
  ruleText: string | null;
  category: string | null;
  policyExcerpt: string | null;
  status: PolicyRuleStatus;
  reviewedByUserId: string | null;
  reviewedByUserName: string | null;
  reviewedAt: string | null;
  reviewerNote: string | null;
  createdAt: string; // ISO 8601
  updatedAt: string;
}

// Wire shape returned by `GET /api/hr/policies/:id/extraction`.
// `summary` lets the HR view render an at-a-glance "3/5 approved" badge
// without iterating the rules list client-side.
export interface PolicyExtractionReport {
  policyId: string;
  // The overall extraction lifecycle. Distinct from a single rule's status:
  // a policy can have 0 rules extracted (e.g. the policy is image-only),
  // in which case `status === 'Ready'` and the rules array is empty — the
  // HR Admin can approve the policy directly because there's nothing to
  // review.
  status: 'Pending' | 'Processing' | 'Ready' | 'Failed' | 'Unavailable' | 'NoRules';
  lastError: string | null;
  processedAt: string | null;
  rules: PolicyExtractedRule[];
  summary: {
    total: number;
    pendingReview: number;
    approved: number;
    rejected: number;
  };
  // True when at least one non-rejected rule is still `Pending_Review` OR
  // the extraction pipeline is still running. The HR UI uses this to
  // disable the policy-approval affordance with a "complete the rule
  // review first" copy.
  blocksPolicyApproval: boolean;
}

// Body shape for `POST /api/hr/policies/:id/extraction/rules/:ruleId/review`.
// HR_Admin only. Editing the rule text is allowed; the ruleKey is
// extractor-assigned and read-only here so re-extraction remains idempotent.
export const ReviewExtractedRuleSchema = z.object({
  decision: z.enum(['approve', 'reject']),
  ruleText: z.string().trim().min(1).max(2000).optional(),
  reviewerNote: z.string().trim().max(2000).optional(),
});
export type ReviewExtractedRuleBody = z.infer<typeof ReviewExtractedRuleSchema>;
